import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps,
  IoperationalLocation,
  ToperationalLocationRequest,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Represents an operational location in the system.
 * This class provides methods to manage and track operational locations,
 * including their contact information, shipping details, and geographical data.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template ToperationalLocationRequest - The type definition for operational location properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link OperationalLocation} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const operationalLocation = await OperationalLocation.getAll();
 *
 * const operationalLocation = await OperationalLocation.filterAll({
 *
 * });
 *
 * const operationalLocation = await OperationalLocation.bulkDelete({
 *
 * });
 *
 * const operationalLocation = await OperationalLocation.getOne('resourceId');
 *
 * const operationalLocation = await OperationalLocation.remove();
 *
 * ```
 */
export class OperationalLocation extends DatabaseAuto {
  /** Base API endpoint for operational location operations */
  private static readonly baseUrl = '/operational-location';

  /** Name of the place or facility */
  placeName: IoperationalLocation['placeName'];

  /** Name of the city where the location is situated */
  cityName: IoperationalLocation['cityName'];

  /** Physical address of the location (optional) */
  address?: IoperationalLocation['address'];

  /** Geographical coordinates [longitude, latitude] (optional) */
  coordinates?: IoperationalLocation['coordinates'];

  /** Shipping cost associated with this location */
  shippingCost: IoperationalLocation['shippingCost'];

  /** Estimated delivery time in days */
  deliversInDays: IoperationalLocation['deliversInDays'];

  /** Contact email for the location (optional) */
  contactEmail?: IoperationalLocation['contactEmail'];

  /** Contact phone number for the location (optional) */
  contactPhone?: IoperationalLocation['contactPhone'];

  /**
   * Creates a new instance of OperationalLocation.
   * @param {IoperationalLocation} data - The operational location data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: IoperationalLocation) {
    validateData(data, {
      placeName: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Place name must be a non-empty string'
      },
      cityName: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'City name must be a non-empty string'
      },
      shippingCost: {
        validate: (v) =>
          typeof v === 'number' &&
          v >= 0,
        message: 'Shipping cost must be a non-negative number'
      },
      deliversInDays: {
        validate: (v) =>
          typeof v === 'number' &&
          v >= 0 &&
          v <= 30,
        message: 'Delivery days must be a number between 0 and 30'
      }
    });

    super(data);

    this.placeName = data.placeName;
    this.cityName = data.cityName;
    this.address = data.address;
    this.coordinates = data.coordinates;
    this.shippingCost = data.shippingCost;
    this.deliversInDays = data.deliversInDays;
    this.contactEmail = data.contactEmail;
    this.contactPhone = data.contactPhone;
  }

  /**
   * Retrieves all operational locations with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: OperationalLocation[] }>}
   * An object containing the total count and an array of operational locations
   * @throws {Error} If there's an error fetching the locations
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<IoperationalLocation>>(`${OperationalLocation.baseUrl}?${query}`);
    const locations = await lastValueFrom(observer$);

    return {
      count: locations.count,
      dataArr: locations.data.map(val => new OperationalLocation(val))
    };
  }

  /**
   * Filters operational locations based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: OperationalLocation[] }>}
   * An object containing the total count and an array of filtered locations
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<IoperationalLocation>>(
        `${OperationalLocation.baseUrl}/filter`,
        filter
      );
    const locations = await lastValueFrom(observer$);

    return {
      count: locations.count,
      dataArr: locations.data.map((val) => new OperationalLocation(val))
    };
  }

  /**
   * Retrieves a single operational location by its ID.
   * @param {string} resourceId - The unique identifier of the location to retrieve
   * @returns {Promise<OperationalLocation>} The requested operational location
   * @throws {Error} If the location is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<IoperationalLocation>(`${OperationalLocation.baseUrl}/${resourceId}`);
    const location = await lastValueFrom(observer$);

    return new OperationalLocation(location);
  }

  /**
   * Creates a new operational location record.
   * @param {ToperationalLocationRequest<true>} payload - The location data to create
   * @returns {Promise<OperationalLocation>} The newly created location record
   * @throws {Error} If the creation fails
   */
  static async add(payload: ToperationalLocationRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<IoperationalLocation>(`${OperationalLocation.baseUrl}`, payload);

    return new OperationalLocation(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple operational locations based on the provided criteria.
   * @param {IbulkDelete} payload - The criteria for identifying which locations to delete
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation
   * @throws {Error} If the bulk delete operation fails
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(
        `${OperationalLocation.baseUrl}/bulk-delete`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current operational location with new values.
   * @param {ToperationalLocationRequest} payload - The data to update the location with
   * @returns {Promise<OperationalLocation>} The updated location record
   * @throws {Error} If the update operation fails
   */
  async update(payload: ToperationalLocationRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<IoperationalLocation>(
        `${OperationalLocation.baseUrl}/${this._id}`,
        payload
      );

    return new OperationalLocation(await lastValueFrom(observer$));
  }

  /**
   * Removes the current operational location from the system.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the delete operation
   * @throws {Error} If the deletion fails or the location doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${OperationalLocation.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
